// app/ThemeProvider.tsx — Applies theme to <html> and syncs with system preference

import { useEffect, type ReactNode } from 'react';
import { useAppStore } from '@/store';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useAppStore((s) => s.theme);
  const setResolvedTheme = useAppStore((s) => s.setResolvedTheme);

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    function applyTheme(prefersDark: boolean) {
      const resolved = theme === 'system'
        ? (prefersDark ? 'night' : 'day')
        : theme;

      // Map legacy 'light'/'dark' values to 'day'/'night' for vert-or system
      const dataTheme = resolved === 'light' ? 'day'
                      : resolved === 'dark'  ? 'night'
                      : resolved; // 'day' | 'night' pass through

      root.setAttribute('data-theme', dataTheme);
      setResolvedTheme(resolved);

      // Update theme-color meta for browser chrome tinting (Vert & Or palette)
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.setAttribute(
          'content',
          (['night', 'dark'] as string[]).includes(dataTheme) ? '#051009' : '#FBF7EC'
        );
      }
    }

    // Apply immediately
    applyTheme(mediaQuery.matches);

    // Listen for system theme changes (when theme is 'system')
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') applyTheme(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, setResolvedTheme]);

  return <>{children}</>;
}
